import React, { useCallback } from "react";
import { Col } from "antd";
import { Form, Select, TimePicker, InputNumber } from "formik-antd";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

/**
 * Time component for Day, Hour frequency -> displays time picker
 */
export function TimeSelection({
  format = "HH:mm",
}: {
  format?: "HH:mm" | "HH:mm:ss" | "mm";
}) {
  const { t } = useTranslation();
  const { setFieldTouched, validateField } = useFormikContext();
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        //On close of time picker, set field touched and validate. Validation
        //should be performed manually because clicking 'Now' button does not
        //properly trigger validation
        setFieldTouched("triggerTime", true, false);
        validateField("triggerTime");
      }
    },
    [setFieldTouched, validateField],
  );

  return (
    <>
      <Col span={2}>
        <span className="blockText">{t("translation:at")}</span>
      </Col>
      <Col span={8}>
        <Form.Item name="triggerTime">
          <TimePicker
            onOpenChange={handleOpenChange}
            size="large"
            name="triggerTime"
            format={format}
          />
        </Form.Item>
      </Col>
      <Col span={3} />
    </>
  );
}

/**
 * Time component for Week frequency -> displays time picker and select for days
 * of the week
 */
export function Week() {
  const { t } = useTranslation();
  const format = "HH:mm";
  const { setFieldTouched, validateField } = useFormikContext();
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        //On close of time picker, set field touched and validate. Validation
        //should be performed manually because clicking 'Now' button does not
        //properly trigger validation
        setFieldTouched("triggerTime", true, false);
        validateField("triggerTime");
      }
    },
    [setFieldTouched, validateField],
  );

  return (
    <>
      <Col span={2}>
        <span className="blockText">{t("translation:on")}</span>
      </Col>
      <Col span={6}>
        <Form.Item name="triggerNumber">
          <Select name="triggerNumber" size="large" mode="multiple">
            <Select.Option value="Sunday">
              {t("translation:sunday")}
            </Select.Option>
            <Select.Option value="Monday">
              {t("translation:monday")}
            </Select.Option>
            <Select.Option value="Tuesday">
              {t("translation:tuesday")}
            </Select.Option>
            <Select.Option value="Wednesday">
              {t("translation:wednesday")}
            </Select.Option>
            <Select.Option value="Thursday">
              {t("translation:thursday")}
            </Select.Option>
            <Select.Option value="Friday">
              {t("translation:friday")}
            </Select.Option>
            <Select.Option value="Saturday">
              {t("translation:saturday")}
            </Select.Option>
          </Select>
        </Form.Item>
      </Col>
      <Col span={2}>
        <span className="blockText">{t("translation:at")}</span>
      </Col>
      <Col span={3}>
        <Form.Item name="triggerTime">
          <TimePicker
            onOpenChange={handleOpenChange}
            size="large"
            name="triggerTime"
            format={format}
          />
        </Form.Item>
      </Col>
    </>
  );
}

/**
 * Time component for Month frequency -> displays time picker and input for day
 * of the month
 */
export function Month() {
  const { t } = useTranslation();
  const format = "HH:mm";
  const { setFieldTouched, validateField } = useFormikContext();
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        //On close of time picker, set field touched and validate. Validation
        //should be performed manually because clicking 'Now' button does not
        //properly trigger validation
        setFieldTouched("triggerTime", true, false);
        validateField("triggerTime");
      }
    },
    [setFieldTouched, validateField],
  );

  return (
    <>
      <Col span={4}>
        <span className="blockText">{t("translation:on_day")}</span>
      </Col>
      <Col span={4}>
        <Form.Item name="triggerNumber">
          <InputNumber
            name="triggerNumber"
            size="large"
            min={1}
            max={31}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Col>
      <Col span={2}>
        <span className="blockText">{t("translation:at")}</span>
      </Col>
      <Col span={3}>
        <Form.Item name="triggerTime">
          <TimePicker
            onOpenChange={handleOpenChange}
            size="large"
            name="triggerTime"
            format={format}
          />
        </Form.Item>
      </Col>
    </>
  );
}

/**
 * Time component for Year frequency -> displays month select, day input and time picker
 */
export function Year() {
  const { t } = useTranslation();
  const format = "HH:mm";
  const { setFieldTouched, validateField } = useFormikContext();
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        //On close of time picker, set field touched and validate. Validation
        //should be performed manually because clicking 'Now' button does not
        //properly trigger validation
        setFieldTouched("triggerTime", true, false);
        validateField("triggerTime");
      }
    },
    [setFieldTouched, validateField],
  );

  return (
    <>
      <Col span={2}>
        <span className="blockText">{t("translation:in")}</span>
      </Col>
      <Col span={3}>
        <Form.Item name="triggerMonth">
          <Select name="triggerMonth" size="large">
            <Select.Option value="1">{t("translation:january")}</Select.Option>
            <Select.Option value="2">{t("translation:february")}</Select.Option>
            <Select.Option value="3">{t("translation:march")}</Select.Option>
            <Select.Option value="4">{t("translation:april")}</Select.Option>
            <Select.Option value="5">{t("translation:may")}</Select.Option>
            <Select.Option value="6">{t("translation:june")}</Select.Option>
            <Select.Option value="7">{t("translation:july")}</Select.Option>
            <Select.Option value="8">{t("translation:august")}</Select.Option>
            <Select.Option value="9">
              {t("translation:september")}
            </Select.Option>
            <Select.Option value="10">{t("translation:october")}</Select.Option>
            <Select.Option value="11">
              {t("translation:november")}
            </Select.Option>
            <Select.Option value="12">
              {t("translation:december")}
            </Select.Option>
          </Select>
        </Form.Item>
      </Col>
      <Col span={2}>
        <span className="blockText">{t("translation:on_day")}</span>
      </Col>
      <Col span={2}>
        <Form.Item name="triggerNumber">
          <InputNumber
            name="triggerNumber"
            size="large"
            min={1}
            max={31}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Col>
      <Col span={1}>
        <span className="blockText">{t("translation:at")}</span>
      </Col>
      <Col span={3}>
        <Form.Item name="triggerTime">
          <TimePicker
            onOpenChange={handleOpenChange}
            size="large"
            name="triggerTime"
            format={format}
          />
        </Form.Item>
      </Col>
    </>
  );
}
